import { t } from "@lingui/macro";
import type { ToolTypeValue } from "@reactive-resume/dto";
import { Button, Input } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";
import { z } from "zod";

import { joinToolWaitlist, recordToolCtaClick } from "../_utils";

type Props = {
  tool: ToolTypeValue;
  className?: string;
};

export const RateLimitWaitlistForm = ({ tool, className }: Props) => {
  const emailSchema = z.string().email(t`Please enter a valid email address.`);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t`Please enter a valid email address.`);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const result = await joinToolWaitlist(parsed.data);
      recordToolCtaClick(tool, "rate-limit");
      setSuccess(result.alreadyRegistered ? t`You're already on the list.` : t`You're on the list!`);
      setEmail("");
    } catch (submitError) {
      setSuccess("");
      setError(
        submitError instanceof Error ? submitError.message : t`Something went wrong. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={cn("flex w-full flex-col gap-3", className)} onSubmit={onSubmit}>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          type="email"
          value={email}
          hasError={!!error}
          placeholder={t`you@example.com`}
          className="h-11 w-full"
          onChange={(event) => {
            setEmail(event.target.value);
            setError("");
          }}
        />

        <Button type="submit" className="h-11 w-full sm:w-auto" disabled={isSubmitting}>
          {isSubmitting ? t`Joining...` : t`Join the waitlist`}
        </Button>
      </div>

      {error ? <p className="text-sm text-error">{error}</p> : null}
      {success ? <p className="text-sm text-primary">{success}</p> : null}
    </form>
  );
};
